import { useMemo, useState } from "react";
import { naturalSortKey } from "@/utils/naturalSort";
import type { ForecastGroupRef } from "@/features/finances/types/groupForecast";

interface Props {
  groups: ForecastGroupRef[];
  /** group_id → fraction string (e.g. "0.4") */
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  /** group_id → fraction from the previous month (shown as a hint) */
  prevFacts?: Record<string, number>;
  disabled?: boolean;
}

const TILE_COUNT = 10;
const STEP = 0.1;

const TILE_COLORS = [
  "bg-amber-100 border-amber-400 text-amber-900",
  "bg-sky-100 border-sky-400 text-sky-900",
  "bg-emerald-100 border-emerald-400 text-emerald-900",
  "bg-rose-100 border-rose-400 text-rose-900",
  "bg-violet-100 border-violet-400 text-violet-900",
  "bg-orange-100 border-orange-400 text-orange-900",
  "bg-teal-100 border-teal-400 text-teal-900",
  "bg-fuchsia-100 border-fuchsia-400 text-fuchsia-900",
];

function pickColor(idx: number) {
  return TILE_COLORS[idx % TILE_COLORS.length];
}

function parseDecimal(s: string) {
  return parseFloat(s.replace(",", "."));
}

/**
 * Convert fractions map → ordered list of group_id assigned to each of 10 tiles.
 * Tile 0 = "first 10%", ..., 9 = "last 10%". Sorting groups by short_name keeps
 * the layout stable across re-renders. Fractions not snapping to STEP leave the
 * remainder as empty tiles so the user sees what is still free.
 */
function fractionsToTiles(
  value: Record<string, string>,
  groups: ForecastGroupRef[],
): (string | null)[] {
  const tiles: (string | null)[] = Array(TILE_COUNT).fill(null);
  const sortedIds = groups
    .filter((g) => value[g.id] && parseDecimal(value[g.id]) > 0)
    .sort((a, b) => naturalSortKey(a.short_name || a.name).localeCompare(naturalSortKey(b.short_name || b.name)))
    .map((g) => g.id);

  let cursor = 0;
  for (const gid of sortedIds) {
    const frac = parseDecimal(value[gid] ?? "0");
    const slots = Math.round(frac * TILE_COUNT);
    for (let i = 0; i < slots && cursor < TILE_COUNT; i++) {
      tiles[cursor++] = gid;
    }
  }
  return tiles;
}

/** Rebuild value map from tiles (each tile = 10%). */
function tilesToFractions(tiles: (string | null)[]): Record<string, string> {
  const counts: Record<string, number> = {};
  for (const gid of tiles) {
    if (!gid) continue;
    counts[gid] = (counts[gid] ?? 0) + 1;
  }
  const out: Record<string, string> = {};
  for (const [gid, n] of Object.entries(counts)) {
    out[gid] = (n * STEP).toFixed(3);
  }
  return out;
}

export function ForecastGridEditor({
  groups,
  value,
  onChange,
  prevFacts,
  disabled,
}: Props) {
  const tiles = useMemo(() => fractionsToTiles(value, groups), [value, groups]);
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const filledCount = tiles.filter((t) => t !== null).length;
  const emptyCount = TILE_COUNT - filledCount;

  const groupColor = useMemo(() => {
    const sorted = [...groups].sort((a, b) =>
      (a.short_name || a.name).localeCompare(b.short_name || b.name),
    );
    const map: Record<string, string> = {};
    sorted.forEach((g, i) => {
      map[g.id] = pickColor(i);
    });
    return map;
  }, [groups]);

  const fillsForGroup = (gid: string) => tiles.filter((t) => t === gid).length;

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => {
      if (g.id === activeGroup) return true;
      if (fillsForGroup(g.id) > 0) return true;
      return (
        (g.short_name ?? "").toLowerCase().includes(q) ||
        g.name.toLowerCase().includes(q)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, search, activeGroup, tiles]);

  const handleTileClick = (i: number) => {
    if (disabled) return;
    const current = tiles[i];
    if (current) {
      if (current === activeGroup) {
        const next = [...tiles];
        next[i] = null;
        onChange(tilesToFractions(next));
      } else {
        setActiveGroup(current);
      }
    } else {
      if (!activeGroup) return;
      const next = [...tiles];
      next[i] = activeGroup;
      onChange(tilesToFractions(next));
    }
  };

  const handleGroupPick = (gid: string) => {
    if (disabled) return;
    setActiveGroup((cur) => (cur === gid ? null : gid));
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Każdy kafelek = <strong>10%</strong> etatu. Kliknij grupę z listy, by ją
        aktywować, potem puste kafelki, by przypisać. Kliknij wypełniony kafelek,
        by zmienić aktywną grupę; kliknij ponownie, by usunąć.
      </p>

      <div className="grid grid-cols-5 sm:grid-cols-10 gap-1.5" data-testid="forecast-tiles">
        {tiles.map((gid, i) => {
          const grp = gid ? groups.find((g) => g.id === gid) : null;
          const klass = gid
            ? (groupColor[gid] ?? "bg-gray-100 border-gray-300 text-gray-700")
            : "bg-white dark:bg-gray-800 border-dashed border-gray-300 dark:border-gray-600 text-gray-400";
          const isActive = gid && gid === activeGroup;
          return (
            <button
              key={i}
              type="button"
              disabled={disabled}
              onClick={() => handleTileClick(i)}
              data-testid={`forecast-tile-${i}`}
              className={`h-14 rounded border-2 text-xs font-mono flex flex-col items-center justify-center transition ${klass} ${
                isActive ? "ring-2 ring-blue-500 ring-offset-1" : ""
              } ${disabled ? "cursor-default" : "cursor-pointer hover:opacity-80"}`}
              title={grp ? `${grp.short_name || grp.name}` : "Puste"}
            >
              <span className="text-[9px] uppercase tracking-widest opacity-60">
                {(i + 1) * 10}%
              </span>
              <span className="text-xs font-semibold truncate max-w-full px-0.5">
                {grp?.short_name || grp?.name?.slice(0, 6) || "—"}
              </span>
            </button>
          );
        })}
      </div>

      <div className="text-xs text-gray-500" data-testid="forecast-grid-summary">
        Wypełnione: <strong>{filledCount * 10}%</strong> · Pozostało:{" "}
        <strong className={emptyCount === 0 ? "text-green-700" : "text-amber-700"}>
          {emptyCount * 10}%
        </strong>
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-2">
        <div className="flex items-center justify-between mb-1.5 gap-3">
          <p className="text-[10px] uppercase tracking-widest text-gray-500">Grupy zleceń</p>
          {groups.length > 6 && (
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Szukaj grupy…"
              data-testid="forecast-grid-search"
              className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 w-44 bg-white dark:bg-gray-800 dark:text-gray-100"
            />
          )}
        </div>
        <ul className="space-y-0.5 max-h-48 overflow-y-auto" data-testid="forecast-groups">
          {filteredGroups.map((g) => {
            const fills = fillsForGroup(g.id);
            const isActive = activeGroup === g.id;
            const dotKlass = groupColor[g.id] ?? "bg-gray-100";
            const prev = prevFacts?.[g.id];
            return (
              <li key={g.id}>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => handleGroupPick(g.id)}
                  data-testid={`forecast-group-${g.short_name || g.id}`}
                  className={`w-full flex items-center justify-between gap-2 text-sm px-2 py-1 rounded ${
                    isActive ? "bg-blue-50 dark:bg-blue-900/30 ring-1 ring-blue-300" : "hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  } ${disabled ? "cursor-default" : "cursor-pointer"}`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className={`inline-block w-3 h-3 rounded-sm border ${dotKlass}`} />
                    <span className="truncate text-gray-700 dark:text-gray-200">
                      {g.short_name ? <span className="font-mono text-xs text-gray-500 mr-1">{g.short_name}</span> : null}
                      {g.name}
                    </span>
                  </span>
                  <span className="shrink-0 flex items-center gap-2">
                    {prev !== undefined && (
                      <span className="font-mono text-[10px] text-gray-400" title="Poprzedni miesiąc">
                        ↩{Math.round(prev * 100)}%
                      </span>
                    )}
                    <span className="font-mono text-xs">{fills > 0 ? `${fills * 10}%` : "—"}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
